// Servicio de Sedes (Corporate)
import apiService from './api';
import { API_ENDPOINTS } from '@/config/api';
import type {
  Sede,
  SedeFormData,
  SedeStats,
  SedeStatsByCountry,
  PaginatedResponse,
  ApiResponse
} from '@/types';

class SedeService {
  /**
   * Obtener lista de sedes
   */
  async getSedes(params?: {
    page?: number;
    limit?: number;
    search?: string;
    city?: string;
    country?: string;
    timezone?: string;
    // active removido en Sprint 13 - Shadow Tables
  }): Promise<PaginatedResponse<Sede>> {
    return apiService.get<PaginatedResponse<Sede>>(
      API_ENDPOINTS.CORPORATE_SEDES,
      { params }
    );
  }

  /**
   * Obtener sede por ID
   */
  async getSede(id: number): Promise<Sede> {
    const response = await apiService.get<ApiResponse<Sede>>(
      API_ENDPOINTS.CORPORATE_SEDE_BY_ID(id)
    );
    return response.data!;
  }

  /**
   * Crear sede
   */
  async createSede(data: SedeFormData): Promise<Sede> {
    const response = await apiService.post<ApiResponse<Sede>>(
      API_ENDPOINTS.CORPORATE_SEDES,
      data
    );
    return response.data!;
  }

  /**
   * Actualizar sede
   */
  async updateSede(id: number, data: Partial<SedeFormData>): Promise<Sede> {
    const response = await apiService.put<ApiResponse<Sede>>(
      API_ENDPOINTS.CORPORATE_SEDE_BY_ID(id),
      data
    );
    return response.data!;
  }

  /**
   * DEPRECADO en Sprint 13 - Shadow Tables
   * Use deleteSede() para mover a papelera o restoreSede() para restaurar
   * @deprecated
   */
  async toggleSedeActive(_id: number): Promise<Sede> {
    throw new Error('toggleSedeActive está deprecado. Use deleteSede() o restoreSede() en su lugar.');
  }

  /**
   * Eliminar sede (soft delete - mover a papelera)
   */
  async deleteSede(id: number): Promise<void> {
    await apiService.delete(API_ENDPOINTS.CORPORATE_SEDE_BY_ID(id));
  }

  /**
   * Obtener sedes eliminadas (papelera)
   */
  async getDeletedSedes(): Promise<Sede[]> {
    const response = await apiService.get<ApiResponse<Sede[]>>(
      `${API_ENDPOINTS.CORPORATE_SEDES}/trash`
    );
    return response.data!;
  }

  /**
   * Restaurar sede de la papelera
   */
  async restoreSede(id: number): Promise<Sede> {
    const response = await apiService.post<ApiResponse<Sede>>(
      `${API_ENDPOINTS.CORPORATE_SEDE_BY_ID(id)}/restore`
    );
    return response.data!;
  }

  /**
   * Eliminar permanentemente una sede (solo desde papelera)
   */
  async deleteSedePermanently(id: number): Promise<void> {
    await apiService.delete(`${API_ENDPOINTS.CORPORATE_SEDE_BY_ID(id)}/permanent`);
  }

  /**
   * Obtener estadísticas generales de sedes
   */
  async getStats(sedeId?: number | null): Promise<SedeStats> {
    const response = await apiService.get<ApiResponse<SedeStats>>(
      API_ENDPOINTS.CORPORATE_SEDES_STATS,
      { params: sedeId ? { sede_id: sedeId } : undefined }
    );
    return response.data!;
  }

  /**
   * Obtener estadísticas de sedes por país
   */
  async getStatsByCountry(): Promise<SedeStatsByCountry[]> {
    const response = await apiService.get<ApiResponse<SedeStatsByCountry[]>>(
      API_ENDPOINTS.CORPORATE_SEDES_STATS_BY_COUNTRY
    );
    return response.data!;
  }

  /**
   * Obtener todas las zonas horarias utilizadas
   */
  async getTimezones(): Promise<string[]> {
    const response = await apiService.get<ApiResponse<string[]>>(
      API_ENDPOINTS.CORPORATE_SEDES_TIMEZONES
    );
    return response.data!;
  }

  /**
   * Obtener sedes con geolocalización configurada
   */
  async getSedesWithGeolocation(): Promise<Sede[]> {
    const response = await apiService.get<ApiResponse<Sede[]>>(
      API_ENDPOINTS.CORPORATE_SEDES_WITH_GEOLOCATION
    );
    return response.data!;
  }

  /**
   * Verificar si una fecha es festivo en la sede
   */
  async checkHoliday(sedeId: number, date: string): Promise<{
    sede_id: number;
    sede_name: string;
    date: string;
    is_holiday: boolean;
    timezone: string;
  }> {
    const response = await apiService.get<ApiResponse<{
      sede_id: number;
      sede_name: string;
      date: string;
      is_holiday: boolean;
      timezone: string;
    }>>(
      API_ENDPOINTS.CORPORATE_SEDE_CHECK_HOLIDAY(sedeId),
      { params: { date } }
    );
    return response.data!;
  }

  /**
   * Resetear el modo RFID bloqueado en una sede (v2.44.0)
   * El próximo hardware sync de cualquier hub anclará el nuevo modo.
   */
  async resetRfidMode(sedeId: number): Promise<{ message: string }> {
    const response = await apiService.post<{ success: boolean; message: string }>(
      `/hubs/sede/${sedeId}/reset-rfid-mode`
    );
    return { message: response.message || '' };
  }

  /**
   * Validar si una ubicación está dentro del geofence de la sede
   */
  async validateGeofence(
    sedeId: number,
    latitude: number,
    longitude: number
  ): Promise<{
    sede_id: number;
    sede_name: string;
    user_location: { latitude: number; longitude: number };
    sede_location: { latitude: number; longitude: number };
    geofence_radius_meters: number;
    is_within_geofence: boolean;
  }> {
    const response = await apiService.post<ApiResponse<{
      sede_id: number;
      sede_name: string;
      user_location: { latitude: number; longitude: number };
      sede_location: { latitude: number; longitude: number };
      geofence_radius_meters: number;
      is_within_geofence: boolean;
    }>>(
      API_ENDPOINTS.CORPORATE_SEDE_VALIDATE_GEOFENCE(sedeId),
      { latitude, longitude }
    );
    return response.data!;
  }
}

export const sedeService = new SedeService();
export default sedeService;
